import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series (based on actual games in project)
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    // Basketball Series
    'basketball-frvr': ['basketbros', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line', 'basket-random', 'basket-champs', 'basket-swooshes', 'basket-battle', 'basket-and-ball', 'flappy-dunk'],
    'basketbros': ['basketball-frvr', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line', 'basket-random', 'basket-champs', 'basket-swooshes', 'basket-battle'],
    'basketball-legends': ['basketball-frvr', 'basketbros', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line', 'basket-random', 'basket-champs', 'basket-swooshes', 'basket-battle'],
    'basketball-stars': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line', 'basket-random', 'basket-champs', 'basket-swooshes', 'basket-battle'],
    'basketball-slam-dunk': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-stars', 'basket-slam-dunk-2', 'basketball-line', 'basket-random', 'basket-champs', 'basket-swooshes', 'basket-battle'],
    'basket-slam-dunk-2': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basketball-line', 'basket-random', 'basket-champs', 'basket-swooshes'],
    'basketball-line': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basket-random', 'basket-champs'],
    'basket-random': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line', 'basket-champs'],
    'basket-champs': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line', 'basket-random'],
    'basket-swooshes': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line'],
    'basket-battle': ['basketball-frvr', 'basketbros', 'basketball-legends', 'basketball-stars', 'basketball-slam-dunk', 'basket-slam-dunk-2', 'basketball-line'],
    'basket-and-ball': ['basketball-frvr', 'basketball-stars', 'basketball-slam-dunk', 'basketball-line', 'basket-random'],

    // Golf Series
    'golf-orbit': ['golf-champions', '8-ball-pool', '8-ball-billiards-classic'],
    'golf-champions': ['golf-orbit', '8-ball-pool', '8-ball-billiards-classic'],

    // Baseball Series
    'baseball-bros': ['football-bros', 'basketbros', 'basketball-stars'],

    // Pool / Billiards Series
    '8-ball-pool': ['8-ball-billiards-classic', 'golf-orbit', 'golf-champions'],
    '8-ball-billiards-classic': ['8-ball-pool', 'golf-orbit', 'golf-champions'],

    // Fireboy and Watergirl Series
    'fireboy-and-watergirl': ['fireboy-and-watergirl-2', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-4', 'fireboy-and-watergirl-5', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-2': ['fireboy-and-watergirl', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-4', 'fireboy-and-watergirl-5', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-3': ['fireboy-and-watergirl', 'fireboy-and-watergirl-2', 'fireboy-and-watergirl-4', 'fireboy-and-watergirl-5', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-4': ['fireboy-and-watergirl', 'fireboy-and-watergirl-2', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-5', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-5': ['fireboy-and-watergirl', 'fireboy-and-watergirl-2', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-4', 'fireboy-and-watergirl-6'],
    'fireboy-and-watergirl-6': ['fireboy-and-watergirl', 'fireboy-and-watergirl-2', 'fireboy-and-watergirl-3', 'fireboy-and-watergirl-4', 'fireboy-and-watergirl-5'],

    // Age of War Series
    'age-of-war': ['age-of-war-2'],
    'age-of-war-2': ['age-of-war'],

    // Bloons Tower Defense Series
    'bloons-tower-defense': ['bloons-td4'],
    'bloons-td4': ['bloons-tower-defense'],

    // Doodle Jump Series
    'doodle-jump': ['doodle-jump-2', 'helix-jump', 'color-tunnel'],
    'doodle-jump-2': ['doodle-jump', 'helix-jump', 'color-tunnel'],

    // Drift Series
    'drift-boss': ['drift-hunters', 'drift-3', 'drift-dudes'],
    'drift-hunters': ['drift-boss', 'drift-3', 'drift-dudes'],
    'drift-3': ['drift-boss', 'drift-hunters', 'drift-dudes'],
    'drift-dudes': ['drift-boss', 'drift-hunters', 'drift-3'],

    // Earn to Die Series
    'earn-to-die': ['earn-to-die-2', 'zombie-tsunami'],
    'earn-to-die-2': ['earn-to-die', 'zombie-tsunami'],

    // Duck Life Series
    'duck-life': ['duck-life-2', 'duck-life-3', 'duck-life-4', 'duck-life-adventure', 'duck-life-battle'],
    'duck-life-2': ['duck-life', 'duck-life-3', 'duck-life-4', 'duck-life-adventure', 'duck-life-battle'],
    'duck-life-3': ['duck-life', 'duck-life-2', 'duck-life-4', 'duck-life-adventure', 'duck-life-battle'],
    'duck-life-4': ['duck-life', 'duck-life-2', 'duck-life-3', 'duck-life-adventure', 'duck-life-battle'],
    'duck-life-adventure': ['duck-life', 'duck-life-2', 'duck-life-3', 'duck-life-4', 'duck-life-battle'],
    'duck-life-battle': ['duck-life', 'duck-life-2', 'duck-life-3', 'duck-life-4', 'duck-life-adventure'],

    // IO Games Series
    'agar-io': ['slither-io', 'paper-io', 'paper-io-2', 'ducklings-io', 'ninja-io'],
    'slither-io': ['agar-io', 'paper-io', 'paper-io-2', 'ducklings-io', 'ninja-io'],
    'paper-io': ['paper-io-2', 'agar-io', 'slither-io', 'ducklings-io', 'ninja-io'],
    'paper-io-2': ['paper-io', 'agar-io', 'slither-io', 'ducklings-io', 'ninja-io'],
    'ducklings-io': ['agar-io', 'slither-io', 'paper-io', 'paper-io-2', 'ninja-io'],
    'ninja-io': ['agar-io', 'slither-io', 'paper-io', 'paper-io-2', 'ducklings-io'],

    // Football Series
    'football-legends': ['football-bros', 'football-masters', 'football-penalty-champions', 'free-kick-classic'],
    'football-bros': ['football-legends', 'football-masters', 'football-penalty-champions', 'free-kick-classic'],
    'football-masters': ['football-legends', 'football-bros', 'football-penalty-champions', 'free-kick-classic'],
    'football-penalty-champions': ['football-legends', 'football-bros', 'football-masters', 'free-kick-classic'],
    'free-kick-classic': ['football-legends', 'football-bros', 'football-masters', 'football-penalty-champions'],

    // Fancy Pants Series
    'fancy-pants': ['fancy-pants-2', 'stickman-hook', 'stickman-parkour'],
    'fancy-pants-2': ['fancy-pants', 'stickman-hook', 'stickman-parkour'],

    // Moto X3M Series
    'moto-x3m': ['moto-x3m-winter', 'moto-x3m-pool-party', 'earn-to-die', 'earn-to-die-2'],
    'moto-x3m-winter': ['moto-x3m', 'moto-x3m-pool-party'],
    'moto-x3m-pool-party': ['moto-x3m', 'moto-x3m-winter'],

    // Paper / Stickman / Parkour Series
    'stickman-hook': ['stickman-fighter', 'stickman-parkour', 'fancy-pants', 'fancy-pants-2'],
    'stickman-fighter': ['stickman-hook', 'stickman-parkour'],
    'stickman-parkour': ['stickman-hook', 'stickman-fighter', 'fancy-pants', 'fancy-pants-2'],

    // Raft Wars Series
    'raft-wars': ['raft-wars-2'],
    'raft-wars-2': ['raft-wars'],

    // Run Series
    'run-3': ['temple-run-2', 'vex-3', 'vex-4', 'vex-5', 'vex-6'],

    // Shell Shockers Series
    'shell-shockers': ['ninja-io', 'agar-io', 'slither-io'],

    // Tank Trouble Series
    'tank-trouble': ['tank-trouble-2'],
    'tank-trouble-2': ['tank-trouble'],

    // Temple Run Series
    'temple-run-2': ['run-3', 'doodle-jump', 'doodle-jump-2'],

    // Vex Series
    'vex-3': ['vex-4', 'vex-5', 'vex-6', 'stickman-parkour'],
    'vex-4': ['vex-3', 'vex-5', 'vex-6', 'stickman-parkour'],
    'vex-5': ['vex-3', 'vex-4', 'vex-6', 'stickman-parkour'],
    'vex-6': ['vex-3', 'vex-4', 'vex-5', 'stickman-parkour'],

    // 2048 Series
    '2048': ['2048-cupcakes', 'water-sort-puzzle', 'tetris'],
    '2048-cupcakes': ['2048', 'water-sort-puzzle', 'tetris'],

    // Puzzle Series
    'water-sort-puzzle': ['2048', '2048-cupcakes', 'tetris', 'cut-the-rope'],
    'cut-the-rope': ['water-sort-puzzle', '2048', '2048-cupcakes'],

    // Endless / Arcade Series
    'flappy-bird': ['doodle-jump', 'doodle-jump-2', 'helix-jump', 'color-tunnel'],
    'flappy-dunk': ['basketball-frvr', 'basketball-stars', 'basketball-slam-dunk'],
    'helix-jump': ['doodle-jump', 'doodle-jump-2', 'color-tunnel', 'flappy-bird'],
    'color-tunnel': ['helix-jump', 'doodle-jump', 'doodle-jump-2'],
    'crossy-road': ['traffic-mania', 'bridge-race', 'zombie-tsunami'],
    'traffic-mania': ['crossy-road', 'bridge-race', 'zombie-tsunami'],
    'bridge-race': ['crossy-road', 'traffic-mania', 'color-tunnel'],
    'zombie-tsunami': ['crossy-road', 'traffic-mania', 'earn-to-die', 'earn-to-die-2'],

    // Happy Wheels / Ragdoll Series
    'happy-wheels': ['ragdoll-hit', 'house-of-hazards', 'moto-x3m'],
    'ragdoll-hit': ['happy-wheels', 'house-of-hazards', 'stickman-fighter'],
    'house-of-hazards': ['happy-wheels', 'ragdoll-hit'],

    // Fall Guys Series
    'fall-beans': ['fall-guys-ultimate-knockout', 'house-of-hazards'],
    'fall-guys-ultimate-knockout': ['fall-beans'],

    // Friday Night Funkin Series
    'friday-night-funkin': ['fall-beans', 'fancy-pants', 'doodle-jump'],

    // Fruit Ninja Series
    'fruit-ninja': ['slice-it-all', 'rabbit-samurai'],
    'slice-it-all': ['fruit-ninja', 'rabbit-samurai'],
    'rabbit-samurai': ['fruit-ninja', 'slice-it-all'],

    // Raft / Battle Series
    'raft-wars': ['raft-wars-2'],
    'raft-wars-2': ['raft-wars'],

    // Tetris Series
    'tetris': ['2048', '2048-cupcakes', 'water-sort-puzzle'],

    // Vex / Parkour
    'vex-3': ['vex-4', 'vex-5', 'vex-6', 'stickman-parkour'],
    'vex-4': ['vex-3', 'vex-5', 'vex-6', 'stickman-parkour'],
    'vex-5': ['vex-3', 'vex-4', 'vex-6', 'stickman-parkour'],
    'vex-6': ['vex-3', 'vex-4', 'vex-5', 'stickman-parkour'],

    // Tank Trouble
    'tank-trouble': ['tank-trouble-2'],
    'tank-trouble-2': ['tank-trouble'],

    // Doodle Jump
    'doodle-jump': ['doodle-jump-2', 'helix-jump', 'flappy-bird'],
    'doodle-jump-2': ['doodle-jump', 'helix-jump', 'flappy-bird'],
  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar based on new categories
    const priorityGames = [
      "basketball-stars",
      "basketball-legends",
      "basketbros",
      "baseball-bros",
      "football-legends",
      "football-bros",
      "8-ball-pool",
      "agar-io",
      "slither-io",
      "shell-shockers",
      "fireboy-and-watergirl",
      "moto-x3m",
      "run-3",
      "stickman-hook",
      "2048"
    ];

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
